'use client';

import { usePathname, useRouter } from '@/i18n/routing';
import { ArrowBottomIcon, CloseIcon } from '@/icons';
import { useQueryString } from '@/lib/hooks';
import { PageProps } from '@/lib/types';
import { createRandomId } from '@/lib/utils';
import { Breadcrumb, Empty, Section } from '@/ui';
import { Content, PaginationWidget } from '@/widgets';
import { Accordion, ComboboxItem, Container, Flex, Select, Skeleton } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import sanitize from 'sanitize-html';
import useSWR from 'swr';
import classnames from './faq.module.css';

type Props = {
  page: PageProps & {
    menu_title: string;
  };
  options: ComboboxItem[];
};

type FAQ = {
  id: number;
  question: string;
  answer: string;
};

type FAQData = {
  data: {
    faq: FAQ[];
    total_page: number;
  };
};

export default function FAQ({ page, options }: Props) {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const createQueryString = useQueryString(searchParams.toString());
  const [activity, setActivity] = useState<ComboboxItem | null>(
    options?.find((option) => option.value == searchParams.get('activity_id')) || null
  );

  const activityId = activity ? { activity_id: activity?.value } : {};

  const { data: faqData, isLoading } = useSWR<FAQData>([
    '/authorities/lists/faq',
    {
      params: { page: searchParams.get('page'), ...activityId },
    },
  ]);

  const data = faqData?.data?.faq;

  const initialDepsId = searchParams.get('active') || data?.[0]?.id?.toString();
  const [activeQuestion, setActiveQuestion] = useState<string | null>(initialDepsId || null);

  const handleComplexChange = (value: string | null) => {
    setActiveQuestion(value);

    if (!value) {
      return router.replace(pathname, { scroll: false });
    }

    const path = pathname + '?' + createQueryString({ active: value?.toString() });
    router.replace(path, { scroll: false });
  };

  function handleActivity(_: string | null, option: ComboboxItem) {
    setActivity(option);
    const path = pathname + '?' + createQueryString({ activity_id: option.value, active: '', page: '' });
    router.replace(path, { scroll: false });
  }

  function clearActivity() {
    setActivity(null);
    const path = pathname + '?' + createQueryString({ activity_id: '' });
    router.replace(path, { scroll: false });
  }

  useEffect(() => {
    if (!searchParams.get('active') && initialDepsId) {
      const path = pathname + '?' + createQueryString({ active: initialDepsId });
      router.replace(path, { scroll: false });
    }
  }, []);

  const breadcrumbItems = [
    { title: page?.breadcrumb, href: `/general-menu/${page?.menu_id}` },
    { title: page?.menu_title, isCurrent: true },
  ];

  return (
    <Container>
      <Section rightMenu={{ isActive: true, id: page?.menu_id, activeItem: { id: page?.menu_item_id } }}>
        <Content>
          <Content.Header>
            <Breadcrumb items={breadcrumbItems} />
          </Content.Header>
          <Content.Title>{page?.menu_title}</Content.Title>
          <Content.Body>
            <Select
              data={options}
              value={activity ? activity?.value : null}
              onChange={handleActivity}
              size="md"
              classNames={{ input: classnames.select, label: classnames.select__label }}
              mb="xl"
              searchable
              clearable
              label={t('Yo‘nalish')}
              placeholder={t('Yo‘nalish')}
              nothingFoundMessage={t("Ma'lumot topilmadi")}
              clearButtonProps={{
                'aria-label': t('{text}ni yopish', { text: t('Yo‘nalish') }),
              }}
              rightSection={activity ? <CloseIcon /> : <ArrowBottomIcon />}
              rightSectionPointerEvents={activity ? 'all' : 'none'}
              rightSectionProps={{
                className: classnames.select__icon,
                onClick: clearActivity,
              }}
            />
            {isLoading ? (
              <Flex direction="column" gap="1px">
                {Array.from({ length: 5 }, () => (
                  <Skeleton key={createRandomId()} width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                ))}
              </Flex>
            ) : data && data?.length > 0 ? (
              <Accordion
                value={activeQuestion}
                onChange={(value) => handleComplexChange(value)}
                chevron={<ArrowBottomIcon />}
                chevronSize={24}
                classNames={classnames}
              >
                {data?.map((item) => (
                  <Accordion.Item key={item?.id} value={item?.id?.toString()}>
                    <Accordion.Control>{item?.question}</Accordion.Control>
                    <Accordion.Panel>
                      <div dangerouslySetInnerHTML={{ __html: sanitize(item?.answer) }}></div>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <Empty />
            )}
            <Suspense>
              <PaginationWidget totalPage={faqData?.data?.total_page} />
            </Suspense>
          </Content.Body>
        </Content>
      </Section>
    </Container>
  );
}
